/**
 * Color System for Neume
 */

/**
 * Scale Degree Colors - Major Keys
 * Manuscript palette — gilt, sage, mediant rose, indigo, cinnabar, submediant, leading
 */
export const SCALE_DEGREE_COLORS_MAJOR = {
  I: '#B6852A',    // gilt — tonic
  ii: '#5C7A52',   // sage — supertonic
  iii: '#9A4438',  // mediant rose
  IV: '#3E5A78',   // indigo — subdominant
  V: '#A23F2A',    // cinnabar — dominant
  vi: '#6E4F66',   // submediant
  vii: '#5A4630',  // leading tone — walnut
} as const;

/**
 * Scale Degree Colors - Minor Keys
 * Mirrors the Manuscript palette so colors match across modes.
 */
export const SCALE_DEGREE_COLORS_MINOR = {
  i: '#B6852A',    // gilt
  ii: '#5C7A52',   // sage
  III: '#9A4438',  // mediant rose
  iv: '#3E5A78',   // indigo
  v: '#A23F2A',    // cinnabar
  VI: '#6E4F66',   // submediant
  VII: '#5A4630',  // walnut
} as const;

/**
 * Key Background Colors
 */
export const KEY_BACKGROUNDS = {
  'C-major': '#F5F1E8',
  'C-minor': '#F5F1E8',
  'G-major': '#E8F4F0',
  'G-minor': '#E8F4F0',
  'D-major': '#FFF9E5',
  'D-minor': '#FFF9E5',
  'A-major': '#F3EDF7',
  'A-minor': '#F3EDF7',
  'E-major': '#FFF0F0',
  'E-minor': '#FFF0F0',
  'B-major': '#E8F6F8',
  'B-minor': '#E8F6F8',
  'F#-major': '#EEF4E8',
  'F#-minor': '#EEF4E8',
  'Db-major': '#FFEEE0',
  'Db-minor': '#FFEEE0',
  'Ab-major': '#EFF0F8',
  'Ab-minor': '#EFF0F8',
  'Eb-major': '#E8F5EC',
  'Eb-minor': '#E8F5EC',
  'Bb-major': '#FFE9E0',
  'Bb-minor': '#FFE9E0',
  'F-major': '#E5F2F8',
  'F-minor': '#E5F2F8',
} as const;

/**
 * UI Chrome Colors — Manuscript ink + accents
 */
export const UI_COLORS = {
  primaryAction: '#B6852A',     // gilt
  secondaryAction: '#6B5A3E',   // walnut
  success: '#B6852A',
  warning: '#8C6420',           // amber
  error: '#A23F2A',             // cinnabar
  textPrimary: '#2E2417',       // india ink
  textSecondary: '#6B5A3E',     // walnut
  textTertiary: '#9C8A66',      // sepia
  backgroundPrimary: '#F4EDDF', // parchment
  backgroundSecondary: '#F8F2E4', // linen
  borderLight: 'rgba(70, 55, 35, 0.10)',
  borderMedium: 'rgba(70, 55, 35, 0.18)',
  borderDark: 'rgba(70, 55, 35, 0.32)',
} as const;

/**
 * Chromatic/Borrowed Chord Indicators
 */
export const CHROMATIC_INDICATORS = {
  shimmerGradientStart: '#6E4F66',  // submediant
  shimmerGradientEnd: '#B6852A',    // gilt
  edgeGlow: '#B6852A',
  shimmerOpacity: 0.3,
  glowOpacity: 0.4,
  glowBlur: 10,
} as const;

/**
 * Animation Colors — Manuscript gold + cinnabar
 */
export const ANIMATION_COLORS = {
  playbackGlow: '#B6852A',      // gilt
  selectionStroke: '#A23F2A',   // cinnabar
  hoverStroke: '#6B5A3E',       // walnut
  dragOverlay: 'rgba(46, 36, 23, 0.15)',
} as const;

/**
 * Get scale degree color based on key mode
 */
export function getScaleDegreeColor(
  degree: number,
  mode: 'major' | 'minor'
): string {
  const romanNumerals: Record<number, string> = mode === 'major'
    ? { 1: 'I', 2: 'ii', 3: 'iii', 4: 'IV', 5: 'V', 6: 'vi', 7: 'vii' }
    : { 1: 'i', 2: 'ii', 3: 'III', 4: 'iv', 5: 'V', 6: 'VI', 7: 'VII' };

  const colors = mode === 'major' ? SCALE_DEGREE_COLORS_MAJOR : SCALE_DEGREE_COLORS_MINOR;
  const key = romanNumerals[degree] as keyof typeof colors;
  return colors[key] || '#888888';
}

/**
 * Get key background color
 */
export function getKeyBackground(
  key: string,
  mode: 'major' | 'minor'
): string {
  const colorKey = `${key}-${mode}` as keyof typeof KEY_BACKGROUNDS;
  return KEY_BACKGROUNDS[colorKey] || KEY_BACKGROUNDS['C-major'];
}

/**
 * Add alpha channel to hex color
 */
export function hexToRgba(hex: string, alpha: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
}

interface RGB { r: number; g: number; b: number; }

function hexToRgb(hex: string): RGB | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
}
